import * as yup from 'yup';

import { HH_MM_PATTERN, SlaConcerns, SlaMarketDayOffsets } from '../../../../shared/constants';
import { createSLATask, updateSLATask, getSLATasks, getDataFeedInventorySummary } from '../../../services';

import type { SlaTaskAssociation, SlaTaskFormValues, SlaTaskRow } from '../../../../shared/types/sla-task-types';

export const SlaTaskFormVariants = {
  ADD: 'add',
  EDIT_TASK: 'edit-task',
  EDIT_ASSOCIATIONS: 'edit-associations',
  EDIT_ALL: 'edit-all',
} as const;

export type SlaTaskFormVariant = typeof SlaTaskFormVariants[keyof typeof SlaTaskFormVariants];

type SlaTaskFormSubmissionValues = Partial<SlaTaskFormValues> & {
  associations?: SlaTaskAssociation[];
};

type LoadSlaTaskReferenceDataOptions = {
  includeFeeds?: boolean;
  includeSchedules?: boolean;
};

export type ExistingSchedule = {
  taskId: string;
  concern: string;
  time: string;
  timezone: string;
};

type SubmitHandlerConfig = {
  action: 'add-task' | 'edit-task' | 'edit-associations';
  submit: (values: SlaTaskFormSubmissionValues) => Promise<SlaTaskRow>;
};

function buildTaskShape(existingSchedules: ExistingSchedule[] = [], currentTaskId?: string) {
  return {
    friendlyName: yup.string().trim().required('Task name is required.'),
    description: yup.string().nullable(),
    internalOnly: yup.boolean(),
    time: yup.string().required('Time is required.').matches(HH_MM_PATTERN, 'Enter a valid time (HH:mm).'),
    timezone: yup
      .string()
      .required('Timezone is required.')
      .test('valid-timezone', 'Select a valid timezone.', (value) => (value ? Intl.supportedValuesOf('timeZone').includes(value) : false))
      .test(
        'unique-time-timezone',
        'A task with this time/timezone already exists for the selected concern.',
        function validateUniqueSchedule(timezone) {
          const time = this.parent?.time;
          const concern = SlaConcerns[0];

          if (!time || !timezone) {
            return true;
          }

          return !existingSchedules.some(
            (schedule) =>
              schedule.taskId !== currentTaskId && schedule.concern === concern && schedule.time === time && schedule.timezone === timezone
          );
        }
      ),
  };
}

const associationsShape = {
  associations: yup
    .array()
    .of(
      yup.object().shape({
        feedId: yup.string().required(),
        serviceId: yup.number().required(),
        marketDayOffset: yup
          .number()
          .oneOf(SlaMarketDayOffsets as unknown as number[], 'Select a valid market-day offset.')
          .required(),
      })
    )
    .min(1, 'Select at least one association.')
    .required('Select at least one association.'),
};

export const defaultSlaTaskInitialValues = {
  friendlyName: '',
  description: '',
  concern: SlaConcerns[0],
  internalOnly: false,
  time: '',
  timezone: '',
  associations: [],
};

export function buildSlaTaskSchema({
  includeTaskFields,
  includeAssociations,
  existingSchedules = [],
  currentTaskId,
}: {
  includeTaskFields: boolean;
  includeAssociations: boolean;
  existingSchedules?: ExistingSchedule[];
  currentTaskId?: string | undefined;
}) {
  return yup.object().shape({
    ...(includeTaskFields ? buildTaskShape(existingSchedules, currentTaskId) : {}),
    ...(includeAssociations ? associationsShape : {}),
  });
}

export async function loadSlaTaskReferenceData({ includeFeeds = true, includeSchedules = false }: LoadSlaTaskReferenceDataOptions = {}) {
  const [feeds, tasks] = await Promise.all([
    includeFeeds ? getDataFeedInventorySummary() : Promise.resolve([]),
    includeSchedules ? getSLATasks() : Promise.resolve([]),
  ]);

  const existingSchedules: ExistingSchedule[] = [];

  if (includeSchedules) {
    tasks.forEach((task) => {
      if (!task.schedule || !task.id) {
        return;
      }

      existingSchedules.push({
        taskId: task.id,
        concern: task.concern ?? SlaConcerns[0],
        time: task.schedule.time,
        timezone: task.schedule.timezone,
      });
    });
  }

  return {
    availableFeeds: feeds ?? [],
    existingSchedules,
  };
}

function buildTaskFieldsPayload(values: SlaTaskFormSubmissionValues) {
  return {
    friendlyName: values.friendlyName ?? '',
    concern: values.concern ?? SlaConcerns[0],
    internalOnly: values.internalOnly ?? false,
    time: values.time ?? '',
    timezone: values.timezone ?? '',
    description: values.description || null,
  };
}

function buildCreateTaskPayload(values: SlaTaskFormSubmissionValues): SlaTaskFormValues {
  return {
    ...buildTaskFieldsPayload(values),
    ...buildAssociationsPayload(values),
  };
}

function buildAssociationsPayload(values: SlaTaskFormSubmissionValues) {
  return {
    associations: values.associations ?? [],
  };
}

function buildUpdateAllPayload(values: SlaTaskFormSubmissionValues) {
  return {
    ...buildTaskFieldsPayload(values),
    ...buildAssociationsPayload(values),
  };
}

export function getSlaTaskSubmitHandler(variant: SlaTaskFormVariant, taskId?: string): SubmitHandlerConfig {
  if (variant !== SlaTaskFormVariants.ADD && !taskId) {
    throw new Error(`Task id is required for variant ${variant}`);
  }

  const handlers: Record<SlaTaskFormVariant, SubmitHandlerConfig> = {
    [SlaTaskFormVariants.ADD]: {
      action: 'add-task',
      submit: (values) => createSLATask(buildCreateTaskPayload(values)),
    },
    [SlaTaskFormVariants.EDIT_TASK]: {
      action: 'edit-task',
      submit: (values) => updateSLATask(taskId!, buildTaskFieldsPayload(values)),
    },
    [SlaTaskFormVariants.EDIT_ASSOCIATIONS]: {
      action: 'edit-associations',
      submit: (values) => updateSLATask(taskId!, buildAssociationsPayload(values)),
    },
    [SlaTaskFormVariants.EDIT_ALL]: {
      action: 'edit-task',
      submit: (values) => updateSLATask(taskId!, buildUpdateAllPayload(values)),
    },
  };

  return handlers[variant];
}
